/**
 * The provider registry is the single source of truth for service OAuth that
 * can be launched by the desktop bridge. Keep this package dependency-free so
 * the browser client and Electron can consume the same runtime contract.
 */
const serviceOAuthInstallPaths = {
  github: '/api/integrations/github/install',
  linear: '/api/integrations/linear/install',
  sentry: '/api/integrations/sentry/install',
  supabase: '/api/integrations/supabase/install',
}

export type DesktopServiceOAuthProvider = keyof typeof serviceOAuthInstallPaths

export type DesktopServiceOAuthRequest = {
  provider: DesktopServiceOAuthProvider
  workspaceId: string
}

export function isDesktopServiceOAuthProvider(
  value: unknown,
): value is DesktopServiceOAuthProvider {
  return (
    typeof value === 'string' && Object.hasOwn(serviceOAuthInstallPaths, value)
  )
}

export function isDesktopServiceOAuthRequest(
  value: unknown,
): value is DesktopServiceOAuthRequest {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 2 &&
    'provider' in value &&
    'workspaceId' in value &&
    isDesktopServiceOAuthProvider(value.provider) &&
    typeof value.workspaceId === 'string' &&
    /^[A-Za-z0-9_-]{1,128}$/.test(value.workspaceId)
  )
}

export function buildDesktopServiceOAuthInstallUrl(
  origin: string,
  request: DesktopServiceOAuthRequest,
): string {
  if (!isDesktopServiceOAuthProvider(request.provider))
    throw new Error('Invalid desktop service OAuth provider')
  if (
    typeof request.workspaceId !== 'string' ||
    !/^[A-Za-z0-9_-]{1,128}$/.test(request.workspaceId)
  )
    throw new Error('Invalid workspace id')
  const url = new URL(serviceOAuthInstallPaths[request.provider], origin)
  url.searchParams.set('workspaceId', request.workspaceId)
  return url.toString()
}
